import { FC, useMemo } from 'react';
import { Key } from 'lucide-react';
import { formatDateTime, toValidDate } from '@libs/frontend-services';
import { OrderStatusEnum, isValidOrderStatus } from '@libs/schemas/src/order';
import { StatusDisplay } from 'components/StatusDisplay';
import { trpc } from 'src/trpc';

// Map status enum values to human-readable labels
const STATUS_LABELS: Record<OrderStatusEnum, string> = {
  [OrderStatusEnum.Created]: 'Order Created',
  [OrderStatusEnum.Cancelled]: 'Order Cancelled',
  [OrderStatusEnum.Completed]: 'Order Completed',
  [OrderStatusEnum.DropOffConfirmed]: 'Drop-off Confirmed',
  [OrderStatusEnum.DropOffScheduled]: 'Drop-off Scheduled',
  [OrderStatusEnum.InTransitDropOff]: 'In Transit (Drop-off)',
  [OrderStatusEnum.InTransitPickup]: 'In Transit (Pickup)',
  [OrderStatusEnum.PaymentFailed]: 'Payment Failed',
  [OrderStatusEnum.PaymentPending]: 'Payment Pending',
  [OrderStatusEnum.PendingDropOff]: 'Pending Drop-off',
  [OrderStatusEnum.PendingPickup]: 'Pending Pickup',
  [OrderStatusEnum.PickupConfirmed]: 'Pickup Confirmed',
  [OrderStatusEnum.PickupRequested]: 'Pickup Requested',
  [OrderStatusEnum.PickupScheduled]: 'Pickup Scheduled',
  [OrderStatusEnum.Restricted]: 'Restricted',
  [OrderStatusEnum.Refunded]: 'Refunded',
  [OrderStatusEnum.RefundFailed]: 'Refund Failed',
  [OrderStatusEnum.WasteProcessing]: 'Waste Processing',
};

// Type for status history with serialized dates (string instead of Date)
type SerializedStatusHistory = Partial<
  Record<
    OrderStatusEnum,
    {
      date: string;
      updatedBy: string;
      adminMasqueradeUserId?: string;
    }
  >
>;

interface StatusHistoryProps {
  statusHistory?: SerializedStatusHistory[];
}

export const StatusHistory: FC<StatusHistoryProps> = ({ statusHistory }) => {
  // Extract unique user IDs from status history (including admin IDs)
  const userIds = useMemo(() => {
    if (!statusHistory || statusHistory.length === 0) return [];

    const ids = new Set<string>();
    statusHistory.forEach((historyItem) =>
      Object.values(historyItem).forEach((data) => {
        if (data?.updatedBy) {
          ids.add(data.updatedBy);
        }
        if (data?.adminMasqueradeUserId) {
          ids.add(data.adminMasqueradeUserId);
        }
      }),
    );
    return Array.from(ids);
  }, [statusHistory]);

  // Fetch user data for all user IDs
  const { data: users = [], isLoading: isLoadingUsers } = trpc.admin.user.getUsersByIds.useQuery(
    userIds,
    {
      enabled: userIds.length > 0,
    },
  );

  // Create a map of user ID to user name
  const userNameMap = useMemo(() => {
    const map = new Map<string, string>();
    users.forEach((user) => {
      if (user && user._id) {
        const firstName = user.name?.first || '';
        const lastName = user.name?.last || '';
        const fullName = `${firstName} ${lastName}`.trim() || user.email || 'Unknown User';
        map.set(user._id.toString(), fullName);
      }
    });
    return map;
  }, [users]);

  if (!statusHistory || statusHistory.length === 0) {
    return (
      <div className="px-2">
        <div className="flex flex-col items-center justify-center p-8 text-center">
          <p className="text-slate-500 text-sm">No status history available</p>
        </div>
      </div>
    );
  }

  // Flatten and sort status history by date, filtering out invalid statuses
  const validHistoryItems: Array<{
    status: OrderStatusEnum;
    date: string;
    updatedBy: string;
    isMasquerading?: boolean;
    adminMasqueradeUserId?: string;
  }> = [];

  statusHistory.forEach((historyItem) => {
    Object.entries(historyItem).forEach(([status, data]) => {
      if (isValidOrderStatus(status) && data) {
        validHistoryItems.push({
          status,
          ...data,
        });
      }
    });
  });

  // Entries with a missing or unparseable date compare as NaN, which makes the sort
  // order non-deterministic. Sink them to the bottom instead of scrambling the timeline.
  const sortedHistory = validHistoryItems.sort((a, b) => {
    const aTime = toValidDate(a.date)?.getTime();
    const bTime = toValidDate(b.date)?.getTime();
    if (aTime === undefined) return bTime === undefined ? 0 : 1;
    if (bTime === undefined) return -1;
    return bTime - aTime;
  });

  return (
    <div className="px-2">
      <div className="flex flex-col relative gap-6 border-l-2 border-slate-100 pt-6 pl-3">
        {sortedHistory.map((item, index) => (
          <div
            className="flex flex-row items-start justify-between"
            key={`${item.status}-${index}`}
          >
            <div className="absolute -left-[5px] w-[8px] h-[8px] bg-slate-300 rounded-full"></div>
            <div className="flex flex-col w-full">
              <div className="flex justify-between items-start w-full mb-1">
                <div className="flex items-center gap-2">
                  <StatusDisplay status={item.status} className="text-xs" />
                  <p className="text-xs text-slate-700 font-medium">{STATUS_LABELS[item.status]}</p>
                </div>
                <div className="text-xs text-right text-slate-500">
                  <p>{formatDateTime(item.date, "MMM d, yyyy 'at' p")}</p>
                </div>
              </div>
              {item.updatedBy && (
                <div className="flex items-center gap-2">
                  <p className="text-xs text-slate-500">
                    Updated by:{' '}
                    {isLoadingUsers
                      ? 'Loading...'
                      : `${userNameMap.get(item.updatedBy) || 'Unknown User'} (${item.updatedBy.slice(-6)})`}
                  </p>
                  {!!item.adminMasqueradeUserId && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 border border-orange-200">
                      <Key className="w-3 h-3 mr-1" />
                      Admin:{' '}
                      {item.adminMasqueradeUserId
                        ? isLoadingUsers
                          ? 'Loading...'
                          : `${userNameMap.get(item.adminMasqueradeUserId) || 'Unknown Admin'} (${item.adminMasqueradeUserId.slice(-6)})`
                        : 'Unknown Admin'}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
